import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import { PlusIcon, ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { TableToolbar } from "../../../../components/utils/TableToolbar";
import { useTableView } from "../../../../components/utils/PMTableView/useTableView";
import {
  RightDrawer,
  DrawerProps,
  DrawerSection,
} from "../../../../components/utils/PMTableView/RightDrawer";
import { formattedDate, updateUrlWithException } from "../../../../components/Utils";
import { getDrivingTripsAsync } from "../../../../slices/driving/trip";
import {
  TripsDataTable,
  getTripColumns,
  tripModule,
} from "../../../../components/dashboard/driving_record/trips/trips-table";

export const DrivingTrips = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const itemState = useSelector((store) => store.drivingTrips);
  const { isLoading, totalCount } = itemState;

  // Prevents double-dispatch when handleLoadMore updates controller.page
  const skipNextDispatchRef = useRef(false);

  const [drawerTrip, setDrawerTrip] = useState(null);

  const [controller, setController] = useState({
    filters: [],
    page: searchParams.get("page") ? parseInt(searchParams.get("page"), 10) : 1,
    per_page: searchParams.get("per_page")
      ? parseInt(searchParams.get("per_page"), 10)
      : 50,
    query: searchParams.get("query") ? searchParams.get("query") : "",
    sort: searchParams.get("sort") ? searchParams.get("sort") : "desc",
    sortBy: searchParams.get("sortBy")
      ? searchParams.get("sortBy")
      : "start_date",
    view: searchParams.get("view") ? searchParams.get("view") : "all",
  });

  useEffect(() => {
    updateUrlWithException(
      {
        page: controller.page,
        per_page: controller.per_page,
        query: controller.query,
        sort: controller.sort,
        sortBy: controller.sortBy,
        view: controller.view,
      },
      "filters"
    );
  }, [controller]);

  // Column definitions + table-view state
  const columns = useMemo(() => getTripColumns(t), [t]);
  const columnMeta = useMemo(
    () =>
      columns.map((c) => ({
        key: c.key,
        label: c.label,
        always: c.always,
        defaultVisible: c.defaultVisible,
        groupable: c.groupable,
      })),
    [columns]
  );
  const groupOptions = useMemo(
    () =>
      columns
        .filter((c) => c.groupable)
        .map((c) => ({ value: c.key, label: c.label })),
    [columns]
  );

  const tableView = useTableView({
    tableKey: "driving_trips",
    columns: columnMeta,
    getSnapshot: () => ({
      query: controller.query,
      sort: controller.sort,
      sortBy: controller.sortBy,
    }),
    applySnapshot: (snap) => {
      setController((c) => ({
        ...c,
        page: 1,
        query: snap.query || "",
        sort: snap.sort || c.sort,
        sortBy: snap.sortBy || c.sortBy,
      }));
    },
  });

  const tableSort = { field: controller.sortBy, dir: controller.sort };
  const handleSort = (field) => {
    setController((c) => ({
      ...c,
      page: 1,
      sortBy: field,
      sort: c.sortBy === field && c.sort === "asc" ? "desc" : "asc",
    }));
  };

  const handleFiltersChange = (filters) => {
    setController((c) => ({ ...c, page: 1, query: filters.query || "" }));
  };

  const getInitialFilters = () => ({ query: controller.query });

  // Infinite scroll
  const handleLoadMore = () => {
    const nextPage = (controller.page || 1) + 1;
    skipNextDispatchRef.current = true;
    dispatch(getDrivingTripsAsync({ ...controller, page: nextPage, append: true }));
    setController((c) => ({ ...c, page: nextPage }));
  };

  const debouncedDispatchAction = debounce((ctrl) => {
    dispatch(getDrivingTripsAsync(ctrl));
  }, 250);

  useEffect(() => {
    if (skipNextDispatchRef.current) {
      skipNextDispatchRef.current = false;
      return;
    }
    debouncedDispatchAction(controller);
    return () => {
      debouncedDispatchAction.cancel();
    };
  }, [controller, dispatch]);

  const drawerModule = drawerTrip ? tripModule(drawerTrip) : null;

  const listCount = totalCount != null ? `${totalCount}` : null;

  return (
    <>
      <DashboardHeader
        variant="list"
        subtitle=""
        actions={[]}
        title={t("Trips")}
        listCount={listCount}
      >
        <div className="p-5 space-y-4">
          {/* Filter card */}
          <ModuleFilter
            filterCard
            disabled={isLoading}
            onFiltersChange={handleFiltersChange}
            initialFilters={getInitialFilters()}
            searchPlaceholder={t("Search...")}
            filterTitle={t("Trips")}
            acceptedStatuses={[]}
          />

          {/* Table card */}
          <div className="overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_6px_22px_rgba(0,0,0,0.07)]">
            <TableToolbar
              groupOptions={groupOptions}
              activeGroup={tableView.groupBy}
              onGroupChange={tableView.setGroupBy}
              columns={columnMeta}
              columnOrder={tableView.order}
              visibleKeys={tableView.visibleKeys}
              onToggleColumn={tableView.toggleColumn}
              onReorderColumn={tableView.reorderColumn}
              onResetColumns={tableView.resetColumns}
              savedViews={tableView.views}
              activeSavedViewId={tableView.activeViewId}
              savedViewDirty={tableView.dirty}
              onApplySavedView={tableView.applyView}
              onSaveAsView={tableView.saveAsView}
              onUpdateView={tableView.updateView}
              onDeleteView={tableView.deleteView}
              density={tableView.density}
              onDensityChange={tableView.setDensity}
            />
            <TripsDataTable
              columns={columns}
              visibleKeys={tableView.visibleKeys}
              widths={tableView.widths}
              onResize={tableView.resizeColumn}
              groupBy={tableView.groupBy}
              sort={tableSort}
              onSort={handleSort}
              itemState={itemState}
              density={tableView.density}
              hasMore={(totalCount || 0) > (itemState?.data?.length || 0)}
              onLoadMore={handleLoadMore}
              onRowClick={(trip) => setDrawerTrip(trip)}
            />
          </div>
        </div>
      </DashboardHeader>

      {/* Row detail pull-out drawer */}
      <RightDrawer
        open={!!drawerTrip}
        onClose={() => setDrawerTrip(null)}
        eyebrow={t("Trips")}
        title={drawerTrip?.start_address || t("Trips")}
        actions={
          drawerModule
            ? [
                {
                  label: t("Module"),
                  icon: ArrowTopRightOnSquareIcon,
                  primary: true,
                  onClick: () => navigate(drawerModule.to),
                },
              ]
            : []
        }
      >
        {drawerTrip && (
          <>
            <DrawerSection title={t("Trips")}>
              <DrawerProps
                items={[
                  {
                    label: t("Date"),
                    value: drawerTrip.start_date
                      ? formattedDate(drawerTrip.start_date)
                      : "—",
                  },
                  {
                    label: t("automile.from"),
                    value: drawerTrip.start_address || "—",
                  },
                  {
                    label: t("automile.to"),
                    value: drawerTrip.end_address || "—",
                  },
                  {
                    label: t("Distance"),
                    value:
                      drawerTrip.distance_kilometers != null
                        ? `${drawerTrip.distance_kilometers} km`
                        : "—",
                  },
                ]}
              />
            </DrawerSection>
            <DrawerSection title={t("Details")}>
              <DrawerProps
                items={[
                  {
                    label: t("Driver"),
                    value: drawerTrip.driver?.displayTitle || "—",
                  },
                  {
                    label: t("Vehicle"),
                    value: drawerTrip.vehicle?.displayTitle || "—",
                  },
                  {
                    label: t("Module"),
                    value: drawerModule ? drawerModule.label || "—" : "—",
                  },
                ]}
              />
            </DrawerSection>
          </>
        )}
      </RightDrawer>
    </>
  );
};
